import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  createKnowledgeEntry,
  deleteKnowledgeEntry,
  fetchBaseKnowledge,
  fetchKnowledgeEntries,
  fetchMergedPreview,
  getRebstockApiDisplayUrl,
  isRebstockKnowledgeConfigured,
  reindexKnowledge,
  updateKnowledgeEntry,
} from "../api/rebstockKnowledgeApi";

const emptyForm = { title: "", category: "", content: "", tags: "" };

function Stat({ label, value, unit }) {
  return (
    <div className="rounded-lg bg-black/25 px-3 py-2 border border-panel-border">
      <p className="text-[9px] uppercase tracking-wider text-muted">{label}</p>
      <p className="text-lg font-semibold text-white">
        {value}
        <span className="text-[10px] font-normal text-muted ml-1">{unit}</span>
      </p>
    </div>
  );
}

export function RebstockKnowledgePanel() {
  const [loading, setLoading] = useState(true);
  const [baseContent, setBaseContent] = useState("");
  const [baseChars, setBaseChars] = useState(0);
  const [entries, setEntries] = useState([]);
  const [mergedChars, setMergedChars] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showBase, setShowBase] = useState(true);

  const configured = isRebstockKnowledgeConfigured();

  const loadAll = useCallback(async () => {
    if (!configured) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [base, list, preview] = await Promise.all([
        fetchBaseKnowledge(),
        fetchKnowledgeEntries(),
        fetchMergedPreview(),
      ]);
      setBaseContent(base.content || "");
      setBaseChars(base.characters || base.content?.length || 0);
      setEntries(list.entries || []);
      setMergedChars(preview.totalCharacters || 0);
    } catch (err) {
      toast.error(err.message || "Failed to load rebstockbot knowledge.");
    } finally {
      setLoading(false);
    }
  }, [configured]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleEdit = (entry) => {
    setEditingId(entry.id);
    setForm({
      title: entry.title || "",
      category: entry.category || "",
      content: entry.content || "",
      tags: Array.isArray(entry.tags) ? entry.tags.join(", ") : "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.content.trim()) {
      toast.error("Content is required.");
      return;
    }
    const tags = form.tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim() || undefined,
        category: form.category.trim() || undefined,
        content: form.content.trim(),
        tags: tags.length ? tags : undefined,
      };

      if (editingId) {
        await updateKnowledgeEntry(editingId, payload);
        toast.success("Entry updated. RAG reindexed.");
      } else {
        await createKnowledgeEntry(payload);
        toast.success("Knowledge added. RAG reindexed.");
      }
      resetForm();
      await loadAll();
    } catch (err) {
      toast.error(err.message || "Save failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this learned entry?")) return;
    try {
      await deleteKnowledgeEntry(id);
      toast.success("Entry deleted. RAG reindexed.");
      if (editingId === id) resetForm();
      await loadAll();
    } catch (err) {
      toast.error(err.message || "Delete failed.");
    }
  };

  const handleReindex = async () => {
    try {
      await reindexKnowledge();
      toast.success("Vector store reindexed.");
    } catch (err) {
      toast.error(err.message || "Reindex failed.");
    }
  };

  if (!configured) {
    return (
      <div className="card border border-amber-500/30 max-w-2xl">
        <h2 className="card-title text-amber-200">Hotel Rebstock — configuration required</h2>
        <p className="text-xs text-muted mt-2 leading-relaxed">
          Add these to <code className="text-sage-light">uni_admin_frontend/.env</code> and
          restart the admin app:
        </p>
        <pre className="mt-3 p-3 rounded-lg bg-black/40 text-[10px] text-sage-light overflow-x-auto">
{`REACT_APP_REBSTOCK_BOT_API_URL=https://rebstockbotnew.oxmite.com
REACT_APP_REBSTOCK_KNOWLEDGE_KEY=your-knowledge-api-key`}
        </pre>
        <p className="text-[10px] text-muted mt-2">
          The key must match <code>KNOWLEDGE_API_KEY</code> on the rebstockbot server. Add the
          admin URL to <code>CORS_ORIGINS</code> on rebstockbot (e.g. http://localhost:3000).
        </p>
      </div>
    );
  }

  return (
    <section className="space-y-4 w-full">
      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="card-title">Hotel Rebstock · knowledge</h2>
            <p className="text-[10px] text-muted mt-0.5 truncate max-w-md">
              {getRebstockApiDisplayUrl()}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-ghost text-[10px]"
              onClick={loadAll}
              disabled={loading}
            >
              Refresh
            </button>
            <button type="button" className="btn-ghost text-[10px]" onClick={handleReindex}>
              Reindex RAG
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
          <Stat
            label="Base knowledge"
            value={loading ? "…" : baseChars.toLocaleString()}
            unit="chars"
          />
          <Stat
            label="Learned entries"
            value={loading ? "…" : String(entries.length)}
            unit="items"
          />
          <Stat
            label="Merged (RAG)"
            value={loading ? "…" : mergedChars.toLocaleString()}
            unit="chars"
          />
        </div>
      </div>

      <div className="card">
        <div>
          <h2 className="card-title">
            {editingId ? "Edit learned entry" : "Add learned knowledge"}
          </h2>
          <p className="card-subtitle">
            Appended to hotelKnowledge.js for RAG (saved to data/learned-knowledge.json)
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3 mt-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="label" htmlFor="rk-title">
                Title (optional)
              </label>
              <input
                id="rk-title"
                className="input w-full"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Spa hours"
                disabled={submitting}
              />
            </div>
            <div className="sm:w-48">
              <label className="label" htmlFor="rk-category">
                Category
              </label>
              <input
                id="rk-category"
                className="input w-full"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                placeholder="facilities"
                disabled={submitting}
              />
            </div>
          </div>
          <div>
            <label className="label" htmlFor="rk-tags">
              Tags (comma-separated)
            </label>
            <input
              id="rk-tags"
              className="input w-full"
              value={form.tags}
              onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
              placeholder="spa, facilities"
              disabled={submitting}
            />
          </div>
          <div>
            <label className="label" htmlFor="rk-content">
              Content
            </label>
            <textarea
              id="rk-content"
              className="input w-full min-h-[120px] resize-y"
              value={form.content}
              onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
              placeholder="Facts the chatbot should learn…"
              disabled={submitting}
              required
            />
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={submitting || loading}
              className="btn-primary flex-1"
            >
              {submitting ? "Saving…" : editingId ? "Update entry" : "Add knowledge"}
            </button>
            {editingId && (
              <button
                type="button"
                className="btn-ghost"
                onClick={resetForm}
                disabled={submitting}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="card">
        <button
          type="button"
          className="w-full flex items-center justify-between text-left"
          onClick={() => setShowBase((s) => !s)}
        >
          <div>
            <h2 className="card-title">Base knowledge (hotelKnowledge.js)</h2>
            <p className="card-subtitle">
              {baseChars.toLocaleString()} chars · read-only
            </p>
          </div>
          <span className="text-muted text-xs">{showBase ? "▼" : "▶"}</span>
        </button>
        {showBase && (
          <textarea
            className="input w-full min-h-[280px] resize-y font-mono text-[11px] leading-relaxed mt-3 opacity-90"
            value={loading ? "Loading…" : baseContent}
            readOnly
            aria-label="Base hotel knowledge"
          />
        )}
      </div>

      <div className="card">
        <div>
          <h2 className="card-title">Learned entries (via API)</h2>
          <p className="card-subtitle">
            Merged with base knowledge when the chatbot answers
          </p>
        </div>
        {loading ? (
          <p className="text-xs text-muted mt-3">Loading entries…</p>
        ) : entries.length === 0 ? (
          <p className="text-xs text-muted mt-3">No learned entries yet. Add one above.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {entries.map((entry) => (
              <li
                key={entry.id}
                className="rounded-lg border border-panel-border px-3 py-2.5"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white truncate">
                      {entry.title || "Untitled"}
                    </p>
                    <p className="text-[10px] text-muted">
                      {entry.category ? `${entry.category} · ` : ""}
                      {new Date(entry.updatedAt || entry.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      type="button"
                      className="btn-ghost text-[10px] py-1 px-2"
                      onClick={() => handleEdit(entry)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn-ghost text-[10px] py-1 px-2 text-rose"
                      onClick={() => handleDelete(entry.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <p className="text-[11px] text-muted mt-2 whitespace-pre-wrap leading-relaxed">
                  {entry.content}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
